// NetCore: the WebSocket wrapper every game built on netcore shares. It
// opens the socket (one at a time, so a retry never leaves a ghost player
// on the server), answers pings, decodes the generic messages (welcome,
// events, roster, resume token) and hands everything else to the game's
// subclass through `onGameMessage`. The server is authoritative; this only
// ships bytes up and down.
import {
  MSG_EVENT,
  MSG_PING,
  MSG_PONG,
  MSG_RESUME,
  MSG_SCORES,
  MSG_WELCOME,
  PING_SIZE,
  SCORES_HEADER_SIZE,
  SETNAME_MAX_SIZE,
  decodeEvent,
  decodePingStamp,
  decodeResume,
  decodeWelcome,
  encodePing,
  encodeSetName,
  readScoresCount,
  readScoresEntry,
  type EventMsg,
  type ScoreEntry,
} from '@platoon/netcore';

export interface NetCoreHandlers {
  onWelcome(id: number, tickRate: number, roomId: string): void;
  onEvent(e: EventMsg): void;
  // Session / handover token: keep it, reconnect with it after a close.
  onResumeToken(hex: string): void;
  onScores(entries: ScoreEntry[]): void;
  onClose(code: number): void;
}

export interface ConnectOptions {
  resume?: string | null; // token from MSG_RESUME
  room?: string | null; // room id (invite link)
  mode?: string | null; // quick-play mode hint
}

export abstract class NetCore {
  protected ws: WebSocket | null = null;
  private readonly welcome = { id: 0, tickRate: 0, roomId: '' };
  private readonly event: EventMsg = { kind: 0, id: 0, id2: 0 };
  private readonly pongBuf = new ArrayBuffer(PING_SIZE);
  private readonly pongView = new DataView(this.pongBuf);

  constructor(
    private readonly protocolVersion: number,
    private readonly core: NetCoreHandlers,
    private readonly path = '/ws',
  ) {}

  // A message type the core does not handle (snapshots, shots, ...).
  protected abstract onGameMessage(type: number, view: DataView): void;

  connect(opts: ConnectOptions = {}): void {
    const old = this.ws;
    if (old) {
      this.ws = null;
      old.onmessage = null;
      old.onclose = null;
      try {
        old.close();
      } catch {
        /* already dead */
      }
    }
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url =
      `${proto}://${location.host}${this.path}?v=${this.protocolVersion}` +
      (opts.resume ? `&resume=${opts.resume}` : '') +
      (opts.room ? `&room=${opts.room}` : '') +
      (opts.mode ? `&mode=${opts.mode}` : '');
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    ws.onmessage = (ev) => this.onMessage(ev.data as ArrayBuffer);
    ws.onclose = (ev) => {
      this.ws = null;
      this.core.onClose(ev.code);
    };
    this.ws = ws;
  }

  // Drop the socket on purpose (dead link detected); onClose fires as usual.
  close(): void {
    this.ws?.close();
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Send raw bytes if connected (the game's encoders write into their own
  // reused buffers and pass a subarray).
  protected send(bytes: Uint8Array): void {
    if (this.connected) this.ws!.send(bytes);
  }

  sendName(name: string): void {
    if (!this.connected) return;
    const bytes = new TextEncoder().encode(name);
    const buf = new ArrayBuffer(SETNAME_MAX_SIZE);
    const len = encodeSetName(new DataView(buf), bytes);
    this.ws!.send(new Uint8Array(buf, 0, len));
  }

  private onMessage(buf: ArrayBuffer): void {
    const view = new DataView(buf);
    if (view.byteLength < 1) return;
    const type = view.getUint8(0);
    switch (type) {
      case MSG_WELCOME:
        if (decodeWelcome(view, this.welcome)) this.core.onWelcome(this.welcome.id, this.welcome.tickRate, this.welcome.roomId);
        break;
      case MSG_EVENT:
        if (decodeEvent(view, this.event)) this.core.onEvent(this.event);
        break;
      case MSG_RESUME: {
        const hex = decodeResume(view);
        if (hex) this.core.onResumeToken(hex);
        break;
      }
      case MSG_PING: {
        // Echo the server's stamp straight back so it can measure our RTT.
        const stamp = decodePingStamp(view);
        if (stamp >= 0 && this.connected) {
          const len = encodePing(this.pongView, MSG_PONG, stamp);
          this.ws!.send(new Uint8Array(this.pongBuf, 0, len));
        }
        break;
      }
      case MSG_SCORES: {
        const count = readScoresCount(view);
        if (count < 0) return;
        // Once a second; allocating here is fine on the client.
        const entries: ScoreEntry[] = [];
        let offset = SCORES_HEADER_SIZE;
        for (let i = 0; i < count; i++) {
          const e: ScoreEntry = { id: 0, team: 0, kills: 0, deaths: 0, ping: 0, flags: 0, name: '' };
          offset = readScoresEntry(view, offset, e);
          if (offset < 0) return; // malformed; drop the whole roster
          entries.push(e);
        }
        this.core.onScores(entries);
        break;
      }
      default:
        this.onGameMessage(type, view);
    }
  }
}
